// client/src/pages/PTO/CoverageEditModal.tsx
// Shared modal for editing hourly Phone Duty or Front Desk coverage on a given
// day. Each row is (employee, time-in, time-out). Rows can be added, edited,
// and deleted. PTO_ADMIN gating is enforced on the server; this modal only
// renders for the admin user.

import React, { useEffect, useMemo, useState } from 'react';
import { X, Plus, Trash2, Pencil } from 'lucide-react';
import type { PTOEmployee } from './ptoTypes';
import { formatDateFull, formatTime12, compareByName } from './ptoUtils';

// ── Time helpers ────────────────────────────────────────────────────────────

/** True if two HH:MM windows overlap (half-open: [start, end)). */
function timesOverlap(aStart: string, aEnd: string, bStart: string, bEnd: string): boolean {
  return aStart < bEnd && aEnd > bStart;
}

/** Returns the gap(s) of `outer` not covered by `inner` (inner must be a sub-range). */
function computeGaps(outerIn: string, outerOut: string, innerIn: string, innerOut: string): { timeIn: string; timeOut: string }[] {
  const gaps: { timeIn: string; timeOut: string }[] = [];
  if (innerIn > outerIn) gaps.push({ timeIn: outerIn, timeOut: innerIn });
  if (innerOut < outerOut) gaps.push({ timeIn: innerOut, timeOut: outerOut });
  return gaps;
}

export interface CoverageSlot {
  id: number;
  staffIndex: string;
  timeIn: string | null;
  timeOut: string | null;
}

export interface DefaultSlot {
  staffIndex: string;
  timeIn: string | null;
  timeOut: string | null;
}

interface CoverageEditModalProps {
  title: string;                       // e.g. "Phone Coverage" or "Front Desk Coverage – Birmingham"
  dateIso: string;                     // YYYY-MM-DD
  slots: CoverageSlot[];               // existing overrides for this date/office
  defaults?: DefaultSlot[];            // read-only standing schedule (front desk only)
  allowDuplicateEmployees?: boolean;   // let the same person be added more than once (front desk)
  allowOverlap?: boolean;              // permit time windows that overlap existing slots (phone duty:
                                       // multiple offices can cover the phones simultaneously)
  employees: PTOEmployee[];
  onAdd: (payload: { staffIndex: string; timeIn: string; timeOut: string }) => Promise<void>;
  onEdit: (id: number, payload: { staffIndex: string; timeIn: string; timeOut: string }) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
  onClose: () => void;
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,.65)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
  backdropFilter: 'blur(4px)',
};

const modalStyle: React.CSSProperties = {
  background: '#151820', border: '1px solid var(--border)', borderRadius: 12,
  width: 540, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto',
  boxShadow: '0 8px 40px rgba(0,0,0,.6)',
};

const headerStyle: React.CSSProperties = {
  padding: '16px 20px', borderBottom: '1px solid var(--border)',
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
};

const bodyStyle: React.CSSProperties = { padding: 20 };
const footerStyle: React.CSSProperties = {
  padding: '14px 20px', borderTop: '1px solid var(--border)',
  display: 'flex', justifyContent: 'flex-end',
};

const labelStyle: React.CSSProperties = {
  fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
  letterSpacing: '.4px', color: 'var(--muted)', marginBottom: 4, display: 'block',
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '6px 9px', border: '1.5px solid var(--border)',
  borderRadius: 6, background: 'rgba(255,255,255,.05)', color: 'var(--text)',
  fontSize: 13, outline: 'none',
};

const selectStyle: React.CSSProperties = {
  ...inputStyle, background: '#1a1f28', cursor: 'pointer',
  colorScheme: 'dark',
};

export default function CoverageEditModal({
  title, dateIso, slots, defaults, allowDuplicateEmployees = false, allowOverlap = false, employees, onAdd, onEdit, onDelete, onClose,
}: CoverageEditModalProps) {
  // Per-row edit state — null when not editing, else { id }
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<{ staffIndex: string; timeIn: string; timeOut: string }>({
    staffIndex: '', timeIn: '', timeOut: '',
  });

  // New-row form state
  const [adding, setAdding] = useState(false);
  const [newForm, setNewForm] = useState<{ staffIndex: string; timeIn: string; timeOut: string }>({
    staffIndex: '', timeIn: '08:00', timeOut: '17:00',
  });

  // Shift-mode picker — only meaningful for front-desk when exactly one default
  // covers this day and there are no existing overrides yet.
  const primaryDefault =
    defaults && defaults.length === 1 && slots.length === 0 ? defaults[0] : null;
  const shiftModeEnabled = primaryDefault !== null && Boolean(primaryDefault.timeIn && primaryDefault.timeOut);
  const [shiftMode, setShiftMode] = useState<'whole' | 'partial'>('whole');

  // When the user opens the add form and a primary default exists, pre-populate
  // the times to match (so "Whole shift" is a one-click action).
  useEffect(() => {
    if (!adding) return;
    if (primaryDefault?.timeIn && primaryDefault?.timeOut) {
      if (shiftMode === 'whole') {
        setNewForm((f) => ({ ...f, timeIn: primaryDefault.timeIn!, timeOut: primaryDefault.timeOut! }));
      } else if (shiftMode === 'partial') {
        // Default partial window to the middle of the default's shift
        setNewForm((f) => ({ ...f, timeIn: '12:00', timeOut: '13:00' }));
      }
    }
  }, [adding, shiftMode, primaryDefault]);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const empMap = useMemo(() => {
    const m = new Map<string, PTOEmployee>();
    for (const e of employees) m.set(e.staffIndex, e);
    return m;
  }, [employees]);

  const sortedEmployees = useMemo(
    () => [...employees].sort(compareByName),
    [employees],
  );

  function startEdit(slot: CoverageSlot) {
    setEditingId(slot.id);
    setEditForm({
      staffIndex: slot.staffIndex,
      timeIn: slot.timeIn ?? '',
      timeOut: slot.timeOut ?? '',
    });
    setError(null);
  }

  /** Find an existing slot whose time window overlaps the proposed [in, out). */
  function findOverlapping(timeIn: string, timeOut: string, excludeId?: number) {
    for (const s of slots) {
      if (excludeId && s.id === excludeId) continue;
      // No times stored = treat as full-day, always conflicts
      const sIn  = s.timeIn  ?? '00:00';
      const sOut = s.timeOut ?? '24:00';
      if (timesOverlap(timeIn, timeOut, sIn, sOut)) return s;
    }
    return null;
  }

  async function commitEdit() {
    if (!editingId) return;
    if (!editForm.staffIndex) { setError('Pick an employee.'); return; }
    if (!editForm.timeIn || !editForm.timeOut) { setError('Time In and Time Out are required.'); return; }
    if (editForm.timeIn >= editForm.timeOut) { setError('Time Out must be after Time In.'); return; }
    if (!allowOverlap) {
      const overlap = findOverlapping(editForm.timeIn, editForm.timeOut, editingId);
      if (overlap) {
        const name = empMap.get(overlap.staffIndex)?.displayName ?? overlap.staffIndex;
        setError(`Time overlaps with ${name} (${formatTime12(overlap.timeIn)} – ${formatTime12(overlap.timeOut)}).`);
        return;
      }
    }
    setBusy(true);
    setError(null);
    try {
      await onEdit(editingId, editForm);
      setEditingId(null);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to save.');
    } finally {
      setBusy(false);
    }
  }

  async function commitAdd() {
    if (!newForm.staffIndex) { setError('Pick an employee.'); return; }
    if (!newForm.timeIn || !newForm.timeOut) { setError('Time In and Time Out are required.'); return; }
    if (newForm.timeIn >= newForm.timeOut) { setError('Time Out must be after Time In.'); return; }
    if (!allowDuplicateEmployees && slots.some((s) => s.staffIndex === newForm.staffIndex)) {
      setError('That employee is already assigned for this day. Edit the existing row instead.');
      return;
    }
    if (!allowOverlap) {
      const overlap = findOverlapping(newForm.timeIn, newForm.timeOut);
      if (overlap) {
        const name = empMap.get(overlap.staffIndex)?.displayName ?? overlap.staffIndex;
        setError(`Time overlaps with ${name} (${formatTime12(overlap.timeIn)} – ${formatTime12(overlap.timeOut)}).`);
        return;
      }
    }

    // Partial-shift mode: validate the new window is inside the default's window
    // and that the new person isn't the default themselves.
    if (shiftModeEnabled && shiftMode === 'partial' && primaryDefault) {
      const dIn  = primaryDefault.timeIn!;
      const dOut = primaryDefault.timeOut!;
      if (newForm.timeIn < dIn || newForm.timeOut > dOut) {
        setError(`Partial shift must fall within the default schedule (${formatTime12(dIn)} – ${formatTime12(dOut)}).`);
        return;
      }
      if (newForm.staffIndex === primaryDefault.staffIndex) {
        setError('Pick a different employee for the partial shift — you selected the default person.');
        return;
      }
    }

    setBusy(true);
    setError(null);
    try {
      // Save the new person's slot first.
      await onAdd(newForm);

      // Partial-shift: also save gap-fill slots so the default person covers
      // the remainder. The display already supports multiple slots per cell.
      if (shiftModeEnabled && shiftMode === 'partial' && primaryDefault) {
        const gaps = computeGaps(primaryDefault.timeIn!, primaryDefault.timeOut!, newForm.timeIn, newForm.timeOut);
        for (const gap of gaps) {
          // eslint-disable-next-line no-await-in-loop -- sequential to keep optimistic state coherent
          await onAdd({
            staffIndex: primaryDefault.staffIndex,
            timeIn:     gap.timeIn,
            timeOut:    gap.timeOut,
          });
        }
      }

      setAdding(false);
      setShiftMode('whole');
      setNewForm({ staffIndex: '', timeIn: '08:00', timeOut: '17:00' });
    } catch (e: any) {
      setError(e?.message ?? 'Failed to add.');
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(id: number) {
    if (!window.confirm('Remove this coverage slot?')) return;
    setBusy(true);
    setError(null);
    try {
      await onDelete(id);
      if (editingId === id) setEditingId(null);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to delete.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={overlayStyle} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={modalStyle}>
        <div style={headerStyle}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>{title}</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>
              {formatDateFull(dateIso)}
            </div>
          </div>
          <button className="btn" style={{ padding: '4px 8px' }} onClick={onClose}>
            <X size={14} />
          </button>
        </div>

        <div style={bodyStyle}>
          {/* Read-only standing schedule (front desk) */}
          {defaults && defaults.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{
                fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                letterSpacing: '.5px', color: 'var(--muted)', marginBottom: 6,
              }}>
                Default Schedule
              </div>
              {defaults.map((d, i) => {
                const emp = empMap.get(d.staffIndex);
                return (
                  <div
                    key={`${d.staffIndex}-${i}`}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '8px 12px', marginBottom: 6,
                      border: '1px dashed var(--border)', borderRadius: 8,
                      background: 'rgba(255,255,255,.015)',
                      opacity: slots.length > 0 ? 0.5 : 0.9,
                    }}
                  >
                    <div style={{ flex: 1, fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>
                      {emp?.displayName ?? d.staffIndex}
                    </div>
                    <div style={{ flex: 1, fontSize: 11, color: 'var(--muted)' }}>
                      {d.timeIn && d.timeOut
                        ? `${formatTime12(d.timeIn)} – ${formatTime12(d.timeOut)}`
                        : 'All day'}
                    </div>
                    <span style={{
                      fontSize: 9, fontWeight: 700, padding: '2px 7px',
                      borderRadius: 4, background: 'rgba(167,179,196,.12)',
                      color: 'var(--muted)', letterSpacing: '.4px',
                    }}>
                      DEFAULT
                    </span>
                  </div>
                );
              })}
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4, fontStyle: 'italic' }}>
                {slots.length > 0
                  ? 'Defaults are replaced by the overrides below for this day.'
                  : 'These people cover by default. Add overrides below to replace them for this day only.'}
              </div>
            </div>
          )}

          {slots.length === 0 && !adding && (!defaults || defaults.length === 0) && (
            <div style={{ fontSize: 13, color: 'var(--muted)', padding: '8px 0 16px' }}>
              No coverage slots for this date yet. Click "Add Coverage" below to assign someone.
            </div>
          )}

          {slots.length > 0 && (
            <div style={{
              fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: '.5px', color: 'var(--muted)', marginBottom: 6,
            }}>
              Overrides for this day
            </div>
          )}

          {/* Existing slots */}
          {slots.map((slot) => {
            const emp = empMap.get(slot.staffIndex);
            const isEditing = editingId === slot.id;
            return (
              <div
                key={slot.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 12px', marginBottom: 8,
                  border: '1px solid var(--border)', borderRadius: 8,
                  background: 'rgba(255,255,255,.02)',
                }}
              >
                {isEditing ? (
                  <>
                    <div style={{ flex: 1.5 }}>
                      <label style={labelStyle}>Employee</label>
                      <select
                        style={selectStyle}
                        value={editForm.staffIndex}
                        onChange={(e) => setEditForm({ ...editForm, staffIndex: e.target.value })}
                      >
                        {sortedEmployees.map((emp) => (
                          <option key={emp.staffIndex} value={emp.staffIndex}>
                            {emp.displayName}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={labelStyle}>Time In</label>
                      <input
                        type="time" style={inputStyle}
                        value={editForm.timeIn}
                        onChange={(e) => setEditForm({ ...editForm, timeIn: e.target.value })}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={labelStyle}>Time Out</label>
                      <input
                        type="time" style={inputStyle}
                        value={editForm.timeOut}
                        onChange={(e) => setEditForm({ ...editForm, timeOut: e.target.value })}
                      />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 14 }}>
                      <button className="btn primary" style={{ padding: '4px 10px', fontSize: 11 }}
                              disabled={busy} onClick={commitEdit}>
                        Save
                      </button>
                      <button className="btn" style={{ padding: '4px 10px', fontSize: 11 }}
                              disabled={busy} onClick={() => setEditingId(null)}>
                        Cancel
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>
                      {emp?.displayName ?? slot.staffIndex}
                    </div>
                    <div style={{ flex: 1, fontSize: 12, color: 'var(--muted)' }}>
                      {slot.timeIn && slot.timeOut
                        ? `${formatTime12(slot.timeIn)} – ${formatTime12(slot.timeOut)}`
                        : 'All day (8 AM – 5 PM)'}
                    </div>
                    <button
                      title="Edit"
                      onClick={() => startEdit(slot)}
                      disabled={busy}
                      style={{
                        all: 'unset', cursor: 'pointer', padding: 6, borderRadius: 6,
                        color: 'var(--muted)',
                      }}
                    >
                      <Pencil size={13} />
                    </button>
                    <button
                      title="Delete"
                      onClick={() => handleDelete(slot.id)}
                      disabled={busy}
                      style={{
                        all: 'unset', cursor: 'pointer', padding: 6, borderRadius: 6,
                        color: '#ff8b8b',
                      }}
                    >
                      <Trash2 size={13} />
                    </button>
                  </>
                )}
              </div>
            );
          })}

          {/* Add-new form */}
          {adding ? (
            <div
              style={{
                display: 'flex', flexDirection: 'column', gap: 10,
                padding: '10px 12px', marginTop: 4,
                border: '1px dashed rgba(122,162,255,.4)', borderRadius: 8,
                background: 'rgba(122,162,255,.04)',
              }}
            >
              {/* Shift-mode picker — front desk only, when a single default covers this day */}
              {shiftModeEnabled && primaryDefault && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <label style={labelStyle}>Shift mode</label>
                  <div style={{ display: 'flex', gap: 14, fontSize: 12 }}>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
                      <input
                        type="radio"
                        checked={shiftMode === 'whole'}
                        onChange={() => setShiftMode('whole')}
                      />
                      Whole shift <span style={{ color: 'var(--muted)' }}>(replaces default)</span>
                    </label>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
                      <input
                        type="radio"
                        checked={shiftMode === 'partial'}
                        onChange={() => setShiftMode('partial')}
                      />
                      Part of shift <span style={{ color: 'var(--muted)' }}>(default covers the rest)</span>
                    </label>
                  </div>
                  {shiftMode === 'partial' && (
                    <div style={{ fontSize: 11, color: 'var(--muted)', fontStyle: 'italic' }}>
                      {empMap.get(primaryDefault.staffIndex)?.displayName ?? primaryDefault.staffIndex} will fill {formatTime12(primaryDefault.timeIn!)} – {formatTime12(primaryDefault.timeOut!)} except for the window you set below.
                    </div>
                  )}
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ flex: 1.5 }}>
                <label style={labelStyle}>Employee</label>
                <select
                  style={selectStyle}
                  value={newForm.staffIndex}
                  onChange={(e) => setNewForm({ ...newForm, staffIndex: e.target.value })}
                >
                  <option value="">Select...</option>
                  {sortedEmployees
                    .filter((e) => allowDuplicateEmployees || !slots.some((s) => s.staffIndex === e.staffIndex))
                    .map((emp) => (
                      <option key={emp.staffIndex} value={emp.staffIndex}>
                        {emp.displayName}
                      </option>
                    ))}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Time In</label>
                <input
                  type="time" style={inputStyle}
                  value={newForm.timeIn}
                  onChange={(e) => setNewForm({ ...newForm, timeIn: e.target.value })}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Time Out</label>
                <input
                  type="time" style={inputStyle}
                  value={newForm.timeOut}
                  onChange={(e) => setNewForm({ ...newForm, timeOut: e.target.value })}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 14 }}>
                <button className="btn primary" style={{ padding: '4px 10px', fontSize: 11 }}
                        disabled={busy} onClick={commitAdd}>
                  Add
                </button>
                <button className="btn" style={{ padding: '4px 10px', fontSize: 11 }}
                        disabled={busy} onClick={() => { setAdding(false); setShiftMode('whole'); setError(null); }}>
                  Cancel
                </button>
              </div>
              </div>
            </div>
          ) : (
            <button
              className="btn"
              onClick={() => { setAdding(true); setError(null); }}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 6,
                fontSize: 12, padding: '6px 12px',
              }}
            >
              <Plus size={13} />
              Add Coverage
            </button>
          )}

          {error && (
            <div style={{ fontSize: 12, color: 'var(--bad)', marginTop: 12 }}>{error}</div>
          )}
        </div>

        <div style={footerStyle}>
          <button className="btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
